import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type CampaignRow,
  type Me,
  type RecoveryAutomationInput,
  type RecoveryHistoryPage,
  type RecoveryOverview,
  type RecoveryTriggerKey,
} from './api';
import { GERI_KAZANIM_ATLAMA, GERI_KAZANIM_METIN, GERI_KAZANIM_SONUC, GERI_KAZANIM_TETIK } from './metin/kampanya';
import { ENGEL_SEBEBI } from './metin/pazarlama';
import { indirimYaz, para, tamSayi, tarihSaat, TANIMSIZ } from './metin/ortak';
import { Alan, AlanSatiri } from './Workflow';

/**
 * Pazarlama: geri kazanım otomasyonları (Ürün Sahibi, 07.10.2026).
 *
 * Üç durum ve ekran hangisinde olduğunu yazıyor: kapalı, açık ve kuru (kimseye gitmez, "kime
 * gidecekti" listesi birikir), canlı. Canlıyı engelleyen bir şey varsa (gönderen adresi, çıkış
 * bağlantısı ayarı) nedeni ekranda; sessizce başlamamak bir açıklama değildir.
 */

const SONUCLAR = ['WOULD_SEND', 'SENT', 'FAILED', 'SKIPPED'] as const;

function hataMetni(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return (caught.reason !== undefined ? GERI_KAZANIM_METIN.ret[caught.reason] : undefined) ?? caught.message;
  return fallback;
}

function atlamaMetni(neden: string | null): string {
  if (neden === null) return TANIMSIZ;
  if (neden.startsWith('CONSENT_BLOCKED:')) {
    const engel = neden.slice('CONSENT_BLOCKED:'.length);
    return `${GERI_KAZANIM_ATLAMA.CONSENT_BLOCKED}: ${ENGEL_SEBEBI[engel] ?? engel}`;
  }
  return GERI_KAZANIM_ATLAMA[neden] ?? neden;
}

type Satir = RecoveryOverview['items'][number];

function Otomasyon({ satir, kampanyalar, yazabilir, hazir, onDegisti }: { satir: Satir; kampanyalar: CampaignRow[]; yazabilir: boolean; hazir: RecoveryOverview['readiness']; onDegisti: () => void }) {
  const metin = GERI_KAZANIM_METIN;
  const [form, setForm] = useState<RecoveryAutomationInput>({
    enabled: satir.enabled,
    dryRun: satir.dryRun,
    waitMinutes: satir.waitMinutes,
    maxAgeDays: satir.maxAgeDays,
    frequencyDays: satir.frequencyDays,
    campaignId: satir.campaignId,
    templates: satir.templates,
    excludedEmails: satir.excludedEmails,
  });
  const [hariclar, setHariclar] = useState(satir.excludedEmails.join('\n'));
  const [sonuc, setSonuc] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [gecmis, setGecmis] = useState<RecoveryHistoryPage | null>(null);
  const [filtre, setFiltre] = useState('');
  const [sayfaNo, setSayfaNo] = useState(1);

  const yukleGecmis = useCallback(async () => {
    try {
      setGecmis(await api.recoveryHistory(satir.trigger, { ...(filtre ? { outcome: filtre } : {}), page: sayfaNo }));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [satir.trigger, filtre, sayfaNo, metin.hata]);

  useEffect(() => {
    void yukleGecmis();
  }, [yukleGecmis]);

  const kaydet = async () => {
    setMesgul(true);
    setHata(null);
    setSonuc(null);
    try {
      await api.updateRecoveryAutomation(satir.trigger, {
        ...form,
        excludedEmails: hariclar.split('\n').map((x) => x.trim()).filter((x) => x !== ''),
      });
      setSonuc(metin.kaydedildi);
      onDegisti();
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    } finally {
      setMesgul(false);
    }
  };

  const calistir = async () => {
    setMesgul(true);
    setHata(null);
    setSonuc(null);
    try {
      const ozet = await api.runRecovery();
      const bu = ozet.find((x) => x.trigger === satir.trigger);
      setSonuc(bu ? `${metin.calistirildi}: ${tamSayi(bu.scanned)}` : metin.calistirildi);
      await yukleGecmis();
      onDegisti();
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    } finally {
      setMesgul(false);
    }
  };

  const sablon = (dil: 'tr' | 'en', alan: 'subject' | 'html' | 'text', deger: string) =>
    setForm({ ...form, templates: { ...form.templates, [dil]: { ...form.templates[dil], [alan]: deger } } });

  const durum = !form.enabled ? metin.kapali : form.dryRun ? metin.kuru : metin.canli;
  const engel = hazir.email ?? hazir.unsubscribe;
  const sayac = satir.stats;
  const son = gecmis ? Math.max(1, Math.ceil(gecmis.total / gecmis.pageSize)) : 1;

  return (
    <section className="content-section" data-testid={`recovery-${satir.trigger}`} aria-labelledby={`recovery-${satir.trigger}-title`}>
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id={`recovery-${satir.trigger}-title`}>{GERI_KAZANIM_TETIK[satir.trigger]}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{form.enabled ? (form.dryRun ? metin.kuruKip : metin.acik) : metin.kapali}</span>
      </div>

      <div className={form.enabled && !form.dryRun ? 'callout callout-ok' : 'callout callout-warn'} role="status" data-testid="recovery-mode">{durum}</div>
      {form.enabled && !form.dryRun && engel ? <div className="error callout" role="alert">{metin.hazirDegil}: {engel}</div> : null}
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {sonuc ? <div className="callout callout-ok" role="status">{sonuc}</div> : null}

      <div className="stat-row" data-testid="recovery-stats">
        <span>{metin.sayac.gonderilen}: <strong>{tamSayi(sayac.sent)}</strong></span>
        <span>{metin.sayac.gidecekti}: <strong>{tamSayi(sayac.wouldSend)}</strong></span>
        <span>{metin.sayac.kuponKullanilan}: <strong>{tamSayi(sayac.couponUsed)}</strong></span>
        <span>{metin.sayac.odenen}: <strong>{tamSayi(sayac.paidAfterSend)}</strong></span>
        <span>{metin.sayac.atlanan}: <strong>{tamSayi(sayac.skipped)}</strong></span>
        <span>{metin.sayac.hatali}: <strong>{tamSayi(sayac.failed)}</strong></span>
      </div>

      <AlanSatiri>
        <label><input type="checkbox" checked={form.enabled} disabled={!yazabilir} data-testid="recovery-enabled" onChange={(olay) => setForm({ ...form, enabled: olay.target.checked })} /> {metin.acik}</label>
        <label><input type="checkbox" checked={form.dryRun} disabled={!yazabilir} data-testid="recovery-dry" onChange={(olay) => setForm({ ...form, dryRun: olay.target.checked })} /> {metin.kuruKip}</label>
        <Alan etiket={metin.bekleme}>
          <input value={String(form.waitMinutes / 60)} disabled={!yazabilir} inputMode="numeric" onChange={(olay) => setForm({ ...form, waitMinutes: Math.round(Number(olay.target.value) * 60) || 0 })} />
        </Alan>
        <Alan etiket={metin.enGec}>
          <input value={String(form.maxAgeDays)} disabled={!yazabilir} inputMode="numeric" onChange={(olay) => setForm({ ...form, maxAgeDays: Number.parseInt(olay.target.value, 10) || 0 })} />
        </Alan>
        <Alan etiket={metin.siklik}>
          <input value={String(form.frequencyDays)} disabled={!yazabilir} inputMode="numeric" onChange={(olay) => setForm({ ...form, frequencyDays: Number.parseInt(olay.target.value, 10) || 0 })} />
        </Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={metin.kuponKampanyasi}>
          <select value={form.campaignId ?? ''} disabled={!yazabilir} data-testid="recovery-campaign" onChange={(olay) => setForm({ ...form, campaignId: olay.target.value === '' ? null : olay.target.value })}>
            <option value="">{metin.kampanyaSec}</option>
            {kampanyalar.map((kampanya) => (
              <option key={kampanya.id} value={kampanya.id}>{kampanya.name} ({indirimYaz(kampanya.discount)})</option>
            ))}
          </select>
        </Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={metin.hariclar} ipucu={metin.hariclarIpucu}>
          <textarea rows={3} value={hariclar} disabled={!yazabilir} onChange={(olay) => setHariclar(olay.target.value)} />
        </Alan>
      </AlanSatiri>

      <details>
        <summary>{metin.sablon}</summary>
        <p className="muted">{metin.sablonIpucu}</p>
        <p className="muted">{metin.gonderen}</p>
        <AlanSatiri>
          <Alan etiket={metin.konuTr}><input value={form.templates.tr.subject} disabled={!yazabilir} onChange={(olay) => sablon('tr', 'subject', olay.target.value)} /></Alan>
          <Alan etiket={metin.konuEn}><input value={form.templates.en.subject} disabled={!yazabilir} onChange={(olay) => sablon('en', 'subject', olay.target.value)} /></Alan>
        </AlanSatiri>
        <AlanSatiri>
          <Alan etiket={metin.govdeTr}><textarea rows={6} value={form.templates.tr.html} disabled={!yazabilir} onChange={(olay) => sablon('tr', 'html', olay.target.value)} /></Alan>
          <Alan etiket={metin.govdeEn}><textarea rows={6} value={form.templates.en.html} disabled={!yazabilir} onChange={(olay) => sablon('en', 'html', olay.target.value)} /></Alan>
        </AlanSatiri>
        <AlanSatiri>
          <Alan etiket={metin.duzTr}><textarea rows={3} value={form.templates.tr.text} disabled={!yazabilir} onChange={(olay) => sablon('tr', 'text', olay.target.value)} /></Alan>
          <Alan etiket={metin.duzEn}><textarea rows={3} value={form.templates.en.text} disabled={!yazabilir} onChange={(olay) => sablon('en', 'text', olay.target.value)} /></Alan>
        </AlanSatiri>
      </details>

      {yazabilir ? (
        <div className="toolbar">
          <button type="button" className="birincil" disabled={mesgul} data-testid="recovery-save" onClick={() => void kaydet()}>{mesgul ? metin.kaydediliyor : metin.kaydet}</button>
          <button type="button" disabled={mesgul} data-testid="recovery-run" onClick={() => void calistir()}>{mesgul ? metin.calisiyor : metin.simdiCalistir}</button>
        </div>
      ) : null}

      <h3>{metin.gecmis}</h3>
      <div className="toolbar">
        <label>
          {metin.sutunSonuc}{' '}
          <select value={filtre} onChange={(olay) => { setFiltre(olay.target.value); setSayfaNo(1); }}>
            <option value="">{metin.tumSonuclar}</option>
            {SONUCLAR.map((deger) => <option key={deger} value={deger}>{GERI_KAZANIM_SONUC[deger]}</option>)}
          </select>
        </label>
      </div>
      <div className="table-wrap">
        <table data-testid="recovery-history">
          <thead>
            <tr>
              <th>{metin.sutunSepet}</th>
              <th>{metin.sutunDil}</th>
              <th className="money">{metin.sutunTutar}</th>
              <th>{metin.sutunSonuc}</th>
              <th>{metin.sutunNeden}</th>
              <th>{metin.sutunKupon}</th>
              <th>{metin.sutunKuponKullanildi}</th>
              <th>{metin.sutunOdendi}</th>
              <th>{metin.sutunZaman}</th>
            </tr>
          </thead>
          <tbody>
            {gecmis === null || gecmis.items.length === 0 ? (
              <tr><td colSpan={9} className="muted">{metin.gecmisBos}</td></tr>
            ) : (
              gecmis.items.map((kayit) => (
                <tr key={kayit.id}>
                  <td data-label={metin.sutunSepet}>{kayit.reference}</td>
                  <td data-label={metin.sutunDil}>{kayit.language.toUpperCase()}</td>
                  <td data-label={metin.sutunTutar} className="money">{para(kayit.amountMinor, kayit.currency)}</td>
                  <td data-label={metin.sutunSonuc}>{GERI_KAZANIM_SONUC[kayit.outcome] ?? kayit.outcome}</td>
                  <td data-label={metin.sutunNeden}>{kayit.outcome === 'SKIPPED' ? atlamaMetni(kayit.skipReason) : (kayit.error ?? TANIMSIZ)}</td>
                  <td data-label={metin.sutunKupon}>{kayit.couponCode ?? TANIMSIZ}</td>
                  <td data-label={metin.sutunKuponKullanildi}>{kayit.couponUsed === null ? TANIMSIZ : kayit.couponUsed ? metin.evet : metin.hayir}</td>
                  <td data-label={metin.sutunOdendi}>{kayit.outcome === 'SENT' ? (kayit.cartPaid ? metin.evet : metin.hayir) : TANIMSIZ}</td>
                  <td data-label={metin.sutunZaman}>{tarihSaat(kayit.sentAt ?? kayit.cartCreatedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p>
        <button type="button" disabled={sayfaNo <= 1} onClick={() => setSayfaNo((onceki) => Math.max(1, onceki - 1))}>{metin.onceki}</button>{' '}
        {metin.sayfa} {tamSayi(sayfaNo)}/{tamSayi(son)}{' '}
        <button type="button" disabled={sayfaNo >= son} onClick={() => setSayfaNo((onceki) => onceki + 1)}>{metin.sonraki}</button>
      </p>
    </section>
  );
}

export function MarketingRecoveryPanel({ me }: { me: Me }) {
  const okuyabilir = me.permissions.includes('marketing:campaign:read');
  const yazabilir = me.permissions.includes('marketing:campaign:write');
  const [genel, setGenel] = useState<RecoveryOverview | null>(null);
  const [kampanyalar, setKampanyalar] = useState<CampaignRow[]>([]);
  const [hata, setHata] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    try {
      const [ozet, liste] = await Promise.all([api.recoveryAutomations(), api.campaigns('INTERNAL')]);
      setGenel(ozet);
      setKampanyalar(liste.items);
      setHata(null);
    } catch (caught) {
      setHata(hataMetni(caught, GERI_KAZANIM_METIN.hata));
    }
  }, []);

  useEffect(() => {
    if (okuyabilir) void yukle();
  }, [okuyabilir, yukle]);

  if (!okuyabilir) return null;
  if (hata) return <div className="error callout" role="alert">{hata}</div>;
  if (genel === null) return <p className="muted" role="status">{GERI_KAZANIM_METIN.yukleniyor}</p>;

  // Yalnız ayarlanmış ya da vize tetiği gösterilir: tur ve otel sepeti aynı yapıda, site olay
  // bildirmeye başlayınca ayarlanır.
  const gorunen = genel.items.filter((satir) => satir.trigger === 'VISA_CART' || satir.configured);
  return (
    <>
      {gorunen.map((satir) => (
        <Otomasyon key={satir.trigger} satir={satir} kampanyalar={kampanyalar} yazabilir={yazabilir} hazir={genel.readiness} onDegisti={() => void yukle()} />
      ))}
    </>
  );
}

export type { RecoveryTriggerKey };
